import React, { useMemo } from "react";

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

// Helper to select at most 3 page numbers, always showing current, one before and one after if possible.
function getVisiblePageNumbers(currentPage: number, totalPages: number) {
    if (totalPages <= 3) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage === 1) {
        return [1, 2, 3];
    }
    if (currentPage === totalPages) {
        return [totalPages - 2, totalPages - 1, totalPages].filter(n => n > 0);
    }
    return [currentPage - 1, currentPage, currentPage + 1];
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
    // Get visible page numbers (max 3) - memoized to ensure consistency
    // Must be called before early return to satisfy React Hooks rules
    const visiblePages = useMemo(() => getVisiblePageNumbers(currentPage, totalPages), [currentPage, totalPages]);

    if (totalPages <= 1) return null;

    // Always update page if it's a different (and valid) page. Ignore requests outside valid range.
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        onPageChange(page);
    };

    return (
        <div className="flex items-center justify-between sm:justify-end space-x-2 sm:w-fit w-full">
            <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                className="w-8 h-8 rounded-md border border-[#0E8965] dark:border-darkGreenColor-dark flex items-center justify-center hover:bg-[#0E8965]/10 dark:hover:bg-darkGreenColor-dark/10 transition-colors"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.7528 13.8442L7.49775 10.5833C7.38911 10.4741 7.32813 10.3262 7.32812 10.1721C7.32812 10.018 7.38911 9.87014 7.49775 9.76085L10.7528 6.50001C10.8343 6.41775 10.9386 6.36164 11.0522 6.33885C11.1658 6.31606 11.2836 6.32762 11.3906 6.37205C11.4976 6.41648 11.589 6.49177 11.653 6.58832C11.7171 6.68487 11.7509 6.79832 11.7503 6.91418V13.43C11.7509 13.5459 11.7171 13.6593 11.653 13.7559C11.589 13.8524 11.4976 13.9277 11.3906 13.9721C11.2836 14.0166 11.1658 14.0281 11.0522 14.0053C10.9386 13.9826 10.8343 13.9264 10.7528 13.8442Z" fill="#0E8965" className="dark:fill-darkGreenColor-dark" />
                </svg>
            </button>

            {/* Max 3 page number buttons */}
            <div className="flex space-x-1">
                {visiblePages.map((pageNum) => (
                    <button
                        key={pageNum}
                        type="button"
                        onClick={() => handlePageChange(pageNum)}
                        className={
                            "w-8 h-8 rounded-lg flex items-center justify-center text-[14px] leading-5 font-semibold " +
                            (pageNum === currentPage
                                ? "bg-[#0E8965] dark:bg-darkGreenColor-dark text-white border border-[#0E8965] dark:border-darkGreenColor-dark"
                                : "bg-white dark:bg-background-dark text-[#858D9D] dark:text-textTertiary-dark border border-[#E8E8E9] dark:border-borderPrimary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark")
                        }
                        aria-current={pageNum === currentPage ? 'page' : undefined}
                    >
                        {pageNum}
                    </button>
                ))}
            </div>

            <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                className="w-8 h-8 rounded-md border border-[#0E8965] dark:border-darkGreenColor-dark flex items-center justify-center hover:bg-[#0E8965]/10 dark:hover:bg-darkGreenColor-dark/10 transition-colors"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.25 13.4294V6.91237C8.25002 6.79702 8.28425 6.68426 8.34835 6.58835C8.41245 6.49245 8.50354 6.4177 8.61012 6.37356C8.71669 6.32942 8.83396 6.31787 8.9471 6.34036C9.06024 6.36286 9.16417 6.4184 9.24575 6.49995L12.5042 9.75846C12.6136 9.86785 12.675 10.0162 12.675 10.1709C12.675 10.3256 12.6136 10.4739 12.5042 10.5833L9.24575 13.8418C9.16417 13.9233 9.06024 13.9789 8.9471 14.0014C8.83396 14.0239 8.71669 14.0123 8.61012 13.9682C8.50354 13.924 8.41245 13.8493 8.34835 13.7534C8.28425 13.6575 8.25002 13.5447 8.25 13.4294Z" fill="#0E8965" className="dark:fill-darkGreenColor-dark" />
                </svg>
            </button>
        </div>
    );
};

export default Pagination;
